import styled from "styled-components";
import BackgroundImage from "../../../../assets/images/landing-page/investor-lp-hero.png";
import AppSectionImage from "../../../../assets/images/landing-page/app_section_shadow.webp";
import { colorPalette, typeScale } from "../../../../utils/theme";
import { motion } from "framer-motion";
import ManagdInNumbersCardImage from "../../../../assets/images/landing-page/managd_stats_logo.svg";

export const Container = styled.div``;

// Hero Section
export const HeroSection = styled.section`
  margin: 0;
  padding-top: 100px;
  padding-bottom: 120px;
  background-image: url(${BackgroundImage});
  background-position: top center;
  background-repeat: no-repeat;
  background-size: 500px auto;

  @media (max-width: 767.98px) {
    padding-top: 48px;
    padding-bottom: 48px;
    padding-left: 16px;
    padding-right: 16px;
    background-size: 300px auto;
  }
`;

export const HeroContent = styled.div`
  text-align: center;
  justify-items: center;
`;

export const HeroBadge = styled.p`
  margin-bottom: 14px;
  color: ${colorPalette.blue500};
  ${typeScale.bodyLGMain};
`;

export const HeroTitle = styled.h1`
  margin-bottom: 42px;
  color: ${colorPalette.textMain};
  font-family: "Funnel Display", sans-serif;
  font-size: 4.225rem;
  font-weight: 500;
  line-height: 1;
  letter-spacing: -0.03em;

  @media (max-width: 767.98px) {
    margin-bottom: 8px;
    font-size: 2.125rem;
    line-height: 1.25;
  }
`;

export const HeroSubtitle = styled.p`
  margin-bottom: 42px !important;
  color: ${colorPalette.textDark} !important;
  ${typeScale.bodyMD};

  @media (max-width: 767.98px) {
    margin-bottom: 24px !important;
    color: ${colorPalette.textLight};
    ${typeScale.bodyXS};
  }
`;

export const HeroButtonWrapper = styled.div`
  position: relative;
  width: 270px;
  z-index: 1;
  margin: 0 auto;

  @media (max-width: 767.98px) {
    width: 200px;
  }
`;

// Features Section
export const FeatureSection = styled.section`
  @media (max-width: 767.98px) {
    padding-left: 16px;
    padding-right: 16px;
  }
`;

export const FeatureButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 48px;

  @media (max-width: 767.98px) {
    width: 200px;
    justify-self: center;
  }
`;

export const SectionBadge = styled.p`
  margin-bottom: 4px;
  text-transform: uppercase;
  color: ${colorPalette.blue500};
  ${typeScale.bodyXSMain};

  @media (max-width: 767.98px) {
    margin-bottom: 8px;
  }
`;

export const SectionTitle = styled.h2`
  margin-bottom: 16px;
  color: ${colorPalette.textMain};
  ${typeScale.headingMain};

  @media (max-width: 767.98px) {
    margin-bottom: 12px;
    ${typeScale.headingSMMain};
  }
`;

export const SectionSubtitle = styled.h2`
  margin-bottom: 32px;
  color: ${colorPalette.textDark};
  ${typeScale.bodyLG};

  @media (max-width: 767.98px) {
    margin-bottom: 24px;
    ${typeScale.bodyXS};
  }
`;

export const FeatureCardsWrapper = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-top: 24px;
  padding-bottom: 24px;
  margin-left: -16px; /* Offset the section's padding */
  margin-right: -16px; /* Offset the section's padding */

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const FeatureCard = styled.div`
  height: 100%;
  flex: 1;
  border-radius: 16px;
  padding: 32px;
  background: #ebf1f733;
  border: 1px solid ${colorPalette.blue200};
  box-shadow: 0px 0px 30.27px 0px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(150px);
  text-align: start;
  position: relative;

  @media (max-width: 767.98px) {
    height: auto;
    flex: 0 0 auto;
    width: 85%;

    &:first-child {
      margin-left: 16px;
    }

    &:last-child {
      margin-right: 16px;
    }
  }
`;

export const FeatureIcon = styled.img`
  width: 114px;
  margin-bottom: 16px;
`;

export const FeatureCardIcon = styled.img`
  position: absolute;
  bottom: 16px;
  right: 32px;
  pointer-events: none;
`;

export const FeatureTitle = styled.h4`
  margin-bottom: 12px;
  color: ${colorPalette.textMain};
  ${typeScale.headingSMMain};
`;

export const FeatureDescription = styled.p`
  margin-bottom: 32px;
  width: 70%;
  color: ${colorPalette.textDark};
  ${typeScale.bodyXS};

  @media (max-width: 1200px) {
    width: 100%;
  }
`;

export const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: start;
`;

export const FeatureListItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 8px;
`;

export const FeatureListItemLabel = styled.p`
  margin: 0;
  color: ${colorPalette.textMain};
  ${typeScale.bodySM};

  @media (max-width: 767.98px) {
    ${typeScale.bodyXS};
  }
`;

// App Section
export const AppSection = styled.section`
  padding-top: 80px;
  padding-bottom: 80px;

  @media (max-width: 767.98px) {
    padding-top: 24px;
    padding-bottom: 24px;
    padding-left: 16px;
    padding-right: 16px;
  }
`;

export const AppSectionBackground = styled.div`
  position: relative;
  margin-left: -16px;
  margin-right: -16px;
  overflow: hidden;
  background: url(${AppSectionImage}) no-repeat center;
  background-size: contain;
  backdrop-filter: blur(48.417720794677734px);
`;

export const AppCardsWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "activeIndex",
})<{ activeIndex: number }>`
  display: flex;
  gap: 16px;
  transition: transform 0.3s ease-in-out;
  transform: ${({ activeIndex }) =>
    `translateX(calc(-${activeIndex} * (100% - 40px)))`};

  @media (max-width: 767.98px) {
    gap: 8px;
  }
`;

export const IndicatorWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 12px;
  margin-bottom: 24px;
`;

export const IndicatorDot = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin: 0 4px;
  background-color: ${({ isActive }) =>
    isActive ? colorPalette.blue500 : "#17224D33"};
  transition: background-color 0.3s ease;
  cursor: pointer;
`;

export const AppSectionCard = styled.div`
  padding: 32px;
  background: #ebf1f766;
  border-radius: 16px;
  border: 1.5px solid ${colorPalette.blue200};
  backdrop-filter: blur(150px);
`;

export const AppCard = styled.div`
  height: 100%;
  flex: 1;
  border-radius: 8px;
  border: 1px solid ${colorPalette.blue200};
  text-align: center;
  background: #ebf1f766;
  position: relative;
  overflow: hidden;

  @media (max-width: 767.98px) {
    height: auto;
    flex: 0 0 auto;
    width: 90%;
    padding-top: 16px;

    &:first-child {
      margin-left: 16px;
    }

    &:last-child {
      margin-right: 16px;
    }
  }
`;

export const AppScreen = styled(motion.img)`
  width: 350px;
  filter: drop-shadow(0px 3.52px 17.61px rgba(0, 0, 0, 0.4));
  position: absolute;
  top: -80px;
  left: 50%;
  transform: translateX(-50%);

  @media (max-width: 767.98px) {
    width: 250px;
    filter: drop-shadow(0px 2.88px 14.38px 0px rgba(0, 0, 0, 0.4));
    position: relative;
    top: 0;
    left: auto;
    transform: none;
  }
`;

export const AppScreenBackground = styled.div`
  background: url(${AppSectionImage}) no-repeat center;
  background-size: contain;
  position: relative;
  min-height: 500px;

  @media (max-width: 767.98px) {
    min-height: unset;
    position: static;
  }
`;

export const AppOverlay = styled.div`
  text-align: start;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: white;
  padding: 16px;
  backdrop-filter: blur(10px);
`;

export const AppTitle = styled.h4`
  margin-bottom: 8px;
  color: ${colorPalette.textMain};
  ${typeScale.headingXSMain};
`;

export const AppDescription = styled.p`
  margin: 0;
  color: ${colorPalette.textDark};
  ${typeScale.bodyXS};
`;

export const ClickableList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin-top: 48px;
`;

export const ClickableListItem = styled.li.withConfig({
  shouldForwardProp: (prop) => prop !== "activeIndex",
})<{ isActive: boolean }>`
  padding: 24px 0;
  cursor: pointer;
  border-bottom: 1px solid ${colorPalette.blue200};
  color: ${(props) =>
    props.isActive ? colorPalette.blue500 : colorPalette.textMain};
  transition: color 0.3s ease;

  &:hover {
    color: ${colorPalette.blue500};
  }
`;

export const ItemTitle = styled.h4`
  margin: 0;
  ${typeScale.headingSMMain};
`;

export const ItemDescription = styled.p`
  margin: 0;
  color: ${colorPalette.textDark};
  font-size: ${typeScale.bodySM};
`;

export const AppButtonWrapper = styled.div`
  width: 270px;
  margin-top: 36px;

  @media (max-width: 767.98px) {
    width: 200px;
    margin-top: 8px;
    justify-self: center;
    margin-bottom: 24px;
  }
`;

export const StatsSection = styled.section`
  padding-top: 28px;
  padding-bottom: 16px;

  @media (max-width: 767.98px) {
    padding-top: 24px;
    padding-bottom: 24px;
    padding: 16px;
    padding-right: 16px;
  }
`;

export const ManagdInNumbersCard = styled.div`
  position: relative;
  padding: 32px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.6);
  border: 1.5px solid ${colorPalette.blue200};
  backdrop-filter: blur(150px);
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background: url(${ManagdInNumbersCardImage}) no-repeat bottom right;
    background-size: contain;
    pointer-events: none;
  }

  @media (max-width: 767.98px) {
    padding: 16px;
  }
`;

export const StatsTitle = styled.h2`
  margin-top: 8px;
  margin-bottom: 16px;
  color: ${colorPalette.textMain};
  ${typeScale.headingMain};

  @media (max-width: 767.98px) {
    margin-bottom: 12px;
    ${typeScale.headingSMMain};
  }
`;

export const StatsSubtitle = styled.p`
  margin-bottom: 32px;
  color: ${colorPalette.textDark};
  ${typeScale.bodyMD};

  @media (max-width: 767.98px) {
    ${typeScale.bodyXS};
  }
`;

export const StatCard = styled.div`
  text-align: start;
  margin-bottom: 24px;

  @media (max-width: 767.98px) {
    text-align: start;
  }
`;

export const StatNumber = styled.h3`
  margin-bottom: 8px;
  color: ${colorPalette.textMain};
  ${typeScale.headingXLMain};

  @media (max-width: 767.98px) {
    ${typeScale.headingMain};
  }
`;

export const StatLabel = styled.p`
  margin: 0;
  color: ${colorPalette.textDark};
  ${typeScale.bodyMD};

  @media (max-width: 767.98px) {
    ${typeScale.bodyXS};
  }
`;

export const StatsButtonWrapper = styled.div`
  width: 180px;
  margin-top: 24px;

  @media (max-width: 767.98px) {
    width: 200px;
    margin-top: 8px;
    justify-self: center;
    margin-bottom: 24px;
  }
`;

// How It Works Section
export const HowItWorksSection = styled.section`
  padding-top: 80px;
  padding-bottom: 80px;

  @media (max-width: 767.98px) {
    padding-top: 48px;
    padding-bottom: 48px;
    padding-left: 16px;
    padding-right: 16px;
  }
`;

export const HowItWorksStepsWrapper = styled.div`
  position: relative;
  margin-top: 48px;

  @media (max-width: 767.98px) {
    margin-top: 32px;
  }
`;

export const HowItWorksDesktopLine = styled.div`
  display: none;
`;

export const HowItWorksMobileLine = styled.div`
  position: absolute;
  top: -40px;
  left: 40px;
  height: 115%;
  border: 1px solid;
  border-image-source: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 6.23%,
    #276ef1 38.85%,
    #cfdffc 77.29%,
    rgba(255, 255, 255, 0) 100%
  );
  border-image-slice: 1;
  z-index: 0;

  @media (min-width: 768px) {
    display: none;
  }
`;

export const HowItWorksStep = styled.div`
  position: relative;
  text-align: center;
  z-index: 1;

  @media (max-width: 767.98px) {
    display: flex;
    align-items: center;
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const HowItWorksIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${colorPalette.blue500};
  margin: 0 auto 24px auto;
  position: relative;
  z-index: 10;

  @media (max-width: 767.98px) {
    width: 48px;
    height: 48px;
    margin: 0 12px 0 16px;
    position: relative;
    z-index: 1;
    flex-shrink: 0;
  }
`;

export const HowItWorksStepBox = styled.div`
  position: relative;
  background: #ebf1f766;
  border: 1.5px solid ${colorPalette.blue200};
  border-radius: 16px;
  padding: 32px 16px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.04);
  text-align: center;

  @media (max-width: 767.98px) {
    border: 1px solid ${colorPalette.blue200};
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
    text-align: left;
    flex: 1;
  }
`;

export const HowItWorksStepContent = styled.div`
  @media (max-width: 767.98px) {
    flex: 1;
    padding-top: 8px;
  }
`;

export const HowItWorksStepTitle = styled.h4`
  margin-bottom: 12px;
  color: ${colorPalette.textMain};
  ${typeScale.headingXSMain};

  @media (max-width: 767.98px) {
    margin-bottom: 8px;
  }
`;

export const HowItWorksStepDescription = styled.p`
  margin: 0;
  color: ${colorPalette.textDark};
  ${typeScale.bodyXS};
  max-width: 280px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 767.98px) {
    max-width: none;
    margin-left: 0;
    margin-right: 0;
  }
`;
